import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import API, { formatError } from '../api/client';
import { ArrowLeft, Save, Printer, Plus, X, User, Activity, Apple, Heart, Droplets, ClipboardList, Sparkles, Loader, ChevronDown, ChevronUp, Bell, Mic } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import ReactMarkdown from 'react-markdown';
import PatientSelect from '../components/PatientSelect';
import MedicationAutocomplete from '../components/MedicationAutocomplete';
import VoiceDictation from '../components/VoiceDictation';

const EMPTY_MED = { nom: '', dosage: '', posologie: '', duree: '' };

const EMPTY_FORM = {
  patient_id: '', date: new Date().toISOString().split('T')[0],
  // Vitaux
  poids: '', taille: '', temperature: '', tension_systolique: '', tension_diastolique: '',
  pouls: '', spo2: '', frequence_respiratoire: '', glycemie: '',
  // Motif
  motif_consultation: '', histoire_maladie: '', etat_general: 'Bon état général, conscient et orienté',
  // Examens
  examen_cardiovasculaire: '', examen_respiratoire: '', examen_abdominal: '', examen_neurologique: '',
  examen_orl: '', examen_ophtalmologique: '', examen_dermatologique: '', examen_osteoarticulaire: '',
  examen_gynecologique: '', examen_urologique: '',
  // Diabète
  diabete_type: '', diabete_glycemie_a_jeun: '', diabete_glycemie_post_prandiale: '',
  diabete_hba1c: '', diabete_traitement: '', diabete_complications: '', diabete_objectifs: '',
  // Nutrition
  nutrition_tour_de_taille: '', nutrition_objectif_calorique: '', nutrition_apport_calorique: '',
  nutrition_type_regime: '', nutrition_activite_physique: '', nutrition_objectif_poids: '',
  // HTA
  hta_stade: '', hta_tension_max_cible: '', hta_tension_min_cible: '', hta_traitement: '',
  hta_complications: '', hta_objectifs: '', hta_risque_cardiovasculaire: '',
  // Néphrologie / Insuffisance rénale
  renal_creatinine: '', renal_dfg: '', renal_uree: '', renal_stade: '', renal_proteinurie: '',
  renal_albuminurie: '', renal_kaliemie: '', renal_diurese: '', renal_traitement: '',
  renal_complications: '', renal_objectifs: '',
  // Conclusion
  diagnostic: '', conduite_a_tenir: '', examens_complementaires: '', notes_cliniques: '',
};

export default function ConsultationPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({ ...EMPTY_FORM, patient_id: searchParams.get('patient_id') || '' });
  const [patient, setPatient] = useState(null);
  const [patients, setPatients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savedId, setSavedId] = useState(null);
  // Modules toggles
  const [hasDiabetes, setHasDiabetes] = useState(false);
  const [hasNutrition, setHasNutrition] = useState(false);
  const [hasHTA, setHasHTA] = useState(false);
  const [hasRenal, setHasRenal] = useState(false);
  // Prescription intégrée
  const [prescMeds, setPrescMeds] = useState([]);
  const [prescNotes, setPrescNotes] = useState('');
  const [prescInstr, setPrescInstr] = useState('');
  const [showPrescSection, setShowPrescSection] = useState(false);
  // AI assistant
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiModel, setAiModel] = useState('gpt');
  const [showAiPanel, setShowAiPanel] = useState(false);
  // Reminder quick-add
  const [showReminderQuick, setShowReminderQuick] = useState(false);
  const [quickReminder, setQuickReminder] = useState({ titre: '', date_rappel: '', priorite: 'normal' });

  const imc = form.poids && form.taille ? (parseFloat(form.poids) / (parseFloat(form.taille) / 100) ** 2).toFixed(1) : null;

  useEffect(() => {
    if (!isEdit) {
      API.get('/patients').then(r => setPatients(r.data.data ?? r.data));
    } else {
      API.get(`/consultations/${id}`).then(r => {
        const d = r.data;
        const f = { ...EMPTY_FORM };
        Object.keys(f).forEach(k => { if (d[k] !== undefined && d[k] !== null) f[k] = String(d[k] ?? ''); });
        setForm(f);
        if (d.patient_info) setPatient(d.patient_info);
        // Load module states
        if (d.diabete_type || d.diabete_hba1c || d.diabete_glycemie_a_jeun) setHasDiabetes(true);
        if (d.nutrition_type_regime || d.nutrition_tour_de_taille) setHasNutrition(true);
        if (d.hta_stade || d.hta_traitement) setHasHTA(true);
        if (d.renal_creatinine || d.renal_dfg || d.renal_stade || d.renal_traitement) setHasRenal(true);
        // Load prescription
        if (d.prescriptions?.length > 0) {
          const presc = d.prescriptions[0];
          setPrescMeds(presc.medicaments || []);
          setPrescNotes(presc.notes || '');
          setPrescInstr(presc.instructions || '');
          setShowPrescSection(true);
        }
        setSavedId(d.id);
      });
    }
  }, [id]);

  useEffect(() => {
    const pid = form.patient_id || searchParams.get('patient_id');
    if (pid && pid !== '') {
      API.get(`/patients/${pid}`).then(r => setPatient(r.data)).catch(() => {});
    }
  }, [form.patient_id]);

  const set = f => e => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const handleSave = async () => {
    setError(''); setSuccess('');
    if (!form.patient_id) { setError('Veuillez sélectionner un patient.'); return; }
    setSaving(true);
    try {
      const payload = {};
      const numFloat = ['poids','taille','temperature','glycemie','diabete_glycemie_a_jeun','diabete_glycemie_post_prandiale','diabete_hba1c','nutrition_tour_de_taille','nutrition_objectif_poids','renal_creatinine','renal_dfg','renal_uree','renal_albuminurie','renal_kaliemie'];
      const numInt = ['tension_systolique','tension_diastolique','pouls','spo2','frequence_respiratoire','nutrition_objectif_calorique','nutrition_apport_calorique','hta_tension_max_cible','hta_tension_min_cible'];
      Object.entries(form).forEach(([k, v]) => {
        if (v === '') payload[k] = null;
        else if (numFloat.includes(k)) payload[k] = parseFloat(v) || null;
        else if (numInt.includes(k)) payload[k] = parseInt(v) || null;
        else payload[k] = v;
      });
      payload.patient_id = parseInt(payload.patient_id);
      // Clear module data if toggles off
      if (!hasDiabetes) ['diabete_type','diabete_glycemie_a_jeun','diabete_glycemie_post_prandiale','diabete_hba1c','diabete_traitement','diabete_complications','diabete_objectifs'].forEach(k => payload[k] = null);
      if (!hasNutrition) ['nutrition_tour_de_taille','nutrition_objectif_calorique','nutrition_apport_calorique','nutrition_type_regime','nutrition_activite_physique','nutrition_objectif_poids'].forEach(k => payload[k] = null);
      if (!hasHTA) ['hta_stade','hta_tension_max_cible','hta_tension_min_cible','hta_traitement','hta_complications','hta_objectifs','hta_risque_cardiovasculaire'].forEach(k => payload[k] = null);
      if (!hasRenal) ['renal_creatinine','renal_dfg','renal_uree','renal_stade','renal_proteinurie','renal_albuminurie','renal_kaliemie','renal_diurese','renal_traitement','renal_complications','renal_objectifs'].forEach(k => payload[k] = null);
      // Prescription
      if (showPrescSection && prescMeds.length > 0 && prescMeds[0].nom) {
        payload.prescription_medicaments = prescMeds.filter(m => m.nom);
        payload.prescription_notes = prescNotes;
        payload.prescription_instructions = prescInstr;
      } else {
        payload.prescription_medicaments = null;
      }
      let res;
      if (isEdit) {
        res = await API.put(`/consultations/${id}`, payload);
        setSuccess('Consultation mise à jour.');
      } else {
        res = await API.post('/consultations', payload);
        setSavedId(res.data.id);
        setSuccess('Consultation enregistrée avec succès.');
      }
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const handlePdf = () => {
    const cid = savedId || id;
    if (cid) window.open(`${process.env.REACT_APP_BACKEND_URL}/api/consultations/${cid}/pdf`, '_blank');
  };

  const handleAiAssist = async () => {
    if (!form.patient_id) { setError('Sélectionnez d\'abord un patient.'); return; }
    setAiLoading(true); setAiSuggestion(''); setShowAiPanel(true);
    try {
      const vitals = {
        'Tension artérielle': form.tension_systolique ? `${form.tension_systolique}/${form.tension_diastolique} mmHg` : '',
        'Pouls': form.pouls ? `${form.pouls} bpm` : '',
        'Température': form.temperature ? `${form.temperature}°C` : '',
        'Poids/Taille': form.poids && form.taille ? `${form.poids}kg / ${form.taille}cm` : '',
        'SpO2': form.spo2 ? `${form.spo2}%` : '',
        'Glycémie': form.glycemie ? `${form.glycemie} g/L` : '',
      };
      const r = await API.post('/ai/consult-assist', {
        patient_id: parseInt(form.patient_id),
        symptoms: form.motif_consultation,
        vitals,
        history: form.histoire_maladie,
        model: aiModel,
      });
      setAiSuggestion(r.data.suggestion);
    } catch (e) {
      setAiSuggestion('Erreur lors de la génération de suggestions IA.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleQuickReminder = async () => {
    if (!quickReminder.titre || !quickReminder.date_rappel || !form.patient_id) return;
    await API.post('/reminders', { ...quickReminder, patient_id: parseInt(form.patient_id) });
    setShowReminderQuick(false);
    setQuickReminder({ titre: '', date_rappel: '', priorite: 'normal' });
  };

  const Field = ({ label, children }) => (
    <div className="flex flex-col space-y-1.5">
      <label className="text-xs font-medium text-stone-600">{label}</label>
      {children}
    </div>
  );
  const inp = (field, type='text', placeholder='') => (
    <input type={type} placeholder={placeholder} value={form[field] || ''}
      onChange={set(field)}
      className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" />
  );
  const txt = (field, rows=3, placeholder='') => (
    <textarea rows={rows} placeholder={placeholder} value={form[field] || ''} onChange={set(field)}
      className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" />
  );

  const ModuleToggle = ({ label, icon: Icon, color, checked, onChange }) => (
    <label className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg border-2 cursor-pointer transition-all ${checked ? `border-${color}-400 bg-${color}-50` : 'border-stone-200 bg-white hover:border-stone-300'}`}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="sr-only" />
      <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${checked ? `bg-${color}-500` : 'bg-stone-200'}`}>
        {checked && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
      </div>
      <Icon size={16} className={checked ? `text-${color}-600` : 'text-stone-400'} />
      <span className={`text-sm font-medium ${checked ? `text-${color}-700` : 'text-stone-600'}`}>{label}</span>
    </label>
  );

  return (
    <div className="space-y-5 animate-fade-in-up max-w-5xl mx-auto" data-testid="consultation-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-stone-500 hover:text-stone-700 text-sm font-medium">
          <ArrowLeft size={16} /> Retour
        </button>
        <div className="flex items-center gap-3">
          {error && <span className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-1 rounded-lg">{error}</span>}
          {success && <span className="text-emerald-600 text-sm bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-lg">{success}</span>}
          {(savedId || isEdit) && (
            <button onClick={handlePdf} data-testid="export-consultation-pdf"
              className="flex items-center gap-1.5 px-4 py-2 border border-stone-300 bg-white text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors">
              <Printer size={15} /> Imprimer PDF
            </button>
          )}
          <button
            data-testid="ai-consult-assist-btn"
            onClick={handleAiAssist}
            disabled={aiLoading || !form.patient_id}
            title="Assistant IA"
            className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {aiLoading ? <Loader size={15} className="animate-spin" /> : <Sparkles size={15} />}
            IA Suggère
          </button>
          <button
            data-testid="quick-reminder-btn"
            onClick={() => setShowReminderQuick(v => !v)}
            disabled={!form.patient_id}
            className="flex items-center gap-1.5 px-4 py-2 border border-amber-300 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 text-amber-700 rounded-lg text-sm font-medium transition-colors"
          >
            <Bell size={15} />
            Rappel
          </button>
          <button data-testid="save-consultation-button" onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors">
            <Save size={16} /> {saving ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <h1 className="font-heading text-2xl font-bold text-stone-900">{isEdit ? 'Consultation' : 'Nouvelle Consultation'}</h1>

      {/* Patient selector */}
      <div className="bg-white border border-stone-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <User size={17} className="text-emerald-600" />
          <h2 className="font-heading font-semibold text-stone-800 text-sm">Patient</h2>
        </div>
        {!isEdit ? (
          <PatientSelect
            testId="consultation-patient-select"
            className="w-full max-w-sm"
            value={form.patient_id}
            patients={patients}
            onChange={(id) => setForm(prev => ({ ...prev, patient_id: id }))}
          />
        ) : null}
        {patient && (
          <div className="flex flex-wrap gap-4 mt-2 text-sm">
            <span className="font-semibold text-stone-800">{patient.prenom} {patient.nom}</span>
            {patient.date_naissance && <span className="text-stone-500">{new Date().getFullYear() - new Date(patient.date_naissance).getFullYear()} ans</span>}
            <span className="text-stone-500">{patient.sexe === 'M' ? 'Homme' : 'Femme'}</span>
            {patient.allergies && <span className="text-amber-600 font-medium text-xs bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Allergies: {patient.allergies}</span>}
          </div>
        )}
        <div className="mt-3">
          <Field label="Date">
            <input type="date" value={form.date} onChange={set('date')}
              className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 w-44" />
          </Field>
        </div>
      </div>

      {/* Module toggles */}
      <div className="bg-white border border-stone-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Modules optionnels à inclure dans cette visite</p>
          <div className="flex items-center gap-2">
            <select
              value={aiModel}
              onChange={e => setAiModel(e.target.value)}
              className="text-xs border border-stone-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:border-purple-500 text-stone-600"
            >
              <option value="gpt">GPT-5.2 (OpenAI)</option>
              <option value="gemini">Gemini Flash (Google)</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <ModuleToggle label="Module Diabète" icon={Activity} color="orange" checked={hasDiabetes} onChange={setHasDiabetes} />
          <ModuleToggle label="Module Nutrition" icon={Apple} color="emerald" checked={hasNutrition} onChange={setHasNutrition} />
          <ModuleToggle label="Module HTA" icon={Heart} color="red" checked={hasHTA} onChange={setHasHTA} />
          <ModuleToggle label="Module Néphrologie" icon={Droplets} color="cyan" checked={hasRenal} onChange={setHasRenal} />
          <ModuleToggle label="Ordonnance" icon={ClipboardList} color="blue" checked={showPrescSection} onChange={(v) => { setShowPrescSection(v); if (v && prescMeds.length === 0) setPrescMeds([{ ...EMPTY_MED }]); }} />
        </div>
      </div>

      {/* AI Suggestion Panel */}
      {showAiPanel && (
        <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4" data-testid="ai-suggestion-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-purple-600" />
              <h3 className="font-heading font-semibold text-purple-800 text-sm">Suggestions IA — {aiModel === 'gpt' ? 'GPT-5.2' : 'Gemini Flash'}</h3>
            </div>
            <button onClick={() => setShowAiPanel(false)} className="p-1 text-purple-400 hover:text-purple-600">
              <X size={16} />
            </button>
          </div>
          {aiLoading ? (
            <div className="flex items-center gap-3 text-purple-600">
              <Loader size={18} className="animate-spin" />
              <span className="text-sm">Analyse en cours...</span>
            </div>
          ) : aiSuggestion ? (
            <div className="bg-white rounded-lg border border-purple-200 p-4">
              <div className="prose prose-sm prose-stone max-w-none">
                <ReactMarkdown>{aiSuggestion}</ReactMarkdown>
              </div>
              <p className="text-xs text-purple-400 mt-3">Ces suggestions sont indicatives et doivent être validées par le médecin.</p>
            </div>
          ) : null}
        </div>
      )}

      {/* Quick Reminder Panel */}
      {showReminderQuick && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4" data-testid="quick-reminder-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bell size={15} className="text-amber-600" />
              <h3 className="font-semibold text-amber-800 text-sm">Ajouter un Rappel pour ce Patient</h3>
            </div>
            <button onClick={() => setShowReminderQuick(false)} className="p-1 text-amber-400 hover:text-amber-600"><X size={15} /></button>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <input value={quickReminder.titre} onChange={e => setQuickReminder(f => ({ ...f, titre: e.target.value }))}
              placeholder="Ex: Contrôle bilan lipidique dans 3 mois..."
              className="px-3 py-2 border border-amber-300 rounded-md text-sm bg-white focus:outline-none focus:border-amber-500" />
            <div className="flex gap-3">
              <input type="date" value={quickReminder.date_rappel} onChange={e => setQuickReminder(f => ({ ...f, date_rappel: e.target.value }))}
                className="flex-1 px-3 py-2 border border-amber-300 rounded-md text-sm bg-white focus:outline-none focus:border-amber-500" />
              <select value={quickReminder.priorite} onChange={e => setQuickReminder(f => ({ ...f, priorite: e.target.value }))}
                className="flex-1 px-3 py-2 border border-amber-300 rounded-md text-sm bg-white focus:outline-none">
                <option value="faible">Faible</option>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
              <button onClick={handleQuickReminder} disabled={!quickReminder.titre || !quickReminder.date_rappel}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-md text-sm font-medium transition-colors">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accordion */}
      <Accordion type="multiple" defaultValue={["vitaux", "motif"]}>

        {/* Vitaux */}
        <AccordionItem value="vitaux" className="bg-white border border-stone-200 rounded-xl mb-3 overflow-hidden px-0">
          <AccordionTrigger className="px-5 py-4 font-heading font-semibold text-stone-800 hover:no-underline hover:bg-stone-50">
            Paramètres Vitaux
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Poids (kg)">{inp('poids','number','70')}</Field>
              <Field label="Taille (cm)">{inp('taille','number','170')}</Field>
              <Field label="Température (°C)">{inp('temperature','number','37.0')}</Field>
              <Field label="Glycémie (g/L)">{inp('glycemie','number','1.0')}</Field>
              <Field label="TA Systolique">{inp('tension_systolique','number','120')}</Field>
              <Field label="TA Diastolique">{inp('tension_diastolique','number','80')}</Field>
              <Field label="Pouls (bpm)">{inp('pouls','number','72')}</Field>
              <Field label="SpO2 (%)">{inp('spo2','number','98')}</Field>
              <Field label="FR (c/min)">{inp('frequence_respiratoire','number','16')}</Field>
            </div>
            {imc && (
              <div className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                parseFloat(imc) < 18.5 ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                parseFloat(imc) < 25 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                parseFloat(imc) < 30 ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                'bg-red-50 text-red-700 border border-red-200'}`}>
                IMC: <b>{imc}</b> — {parseFloat(imc) < 18.5 ? 'Maigreur' : parseFloat(imc) < 25 ? 'Normal' : parseFloat(imc) < 30 ? 'Surpoids' : 'Obésité'}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* Motif */}
        <AccordionItem value="motif" className="bg-white border border-stone-200 rounded-xl mb-3 overflow-hidden px-0">
          <AccordionTrigger className="px-5 py-4 font-heading font-semibold text-stone-800 hover:no-underline hover:bg-stone-50">
            Motif & Anamnèse
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-5 space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wide">Motif de consultation</label>
                <VoiceDictation
                  context="motif de consultation"
                  onTranscription={t => setForm(f => ({ ...f, motif_consultation: (f.motif_consultation ? f.motif_consultation + ' ' : '') + t }))}
                />
              </div>
              {txt('motif_consultation', 2, 'Ex: Douleur abdominale, toux, suivi...')}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wide">Histoire de la maladie</label>
                <VoiceDictation
                  context="histoire de la maladie, symptômes, évolution"
                  onTranscription={t => setForm(f => ({ ...f, histoire_maladie: (f.histoire_maladie ? f.histoire_maladie + ' ' : '') + t }))}
                />
              </div>
              {txt('histoire_maladie', 5, 'Décrivez le début, l\'évolution, les symptômes...')}
            </div>
            <Field label="État général">{txt('etat_general', 2)}</Field>
          </AccordionContent>
        </AccordionItem>

        {/* Examen clinique */}
        <AccordionItem value="examen" className="bg-white border border-stone-200 rounded-xl mb-3 overflow-hidden px-0">
          <AccordionTrigger className="px-5 py-4 font-heading font-semibold text-stone-800 hover:no-underline hover:bg-stone-50">
            Examen Clinique
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[['Cardiovasculaire','examen_cardiovasculaire'],['Respiratoire','examen_respiratoire'],['Abdominal','examen_abdominal'],['Neurologique','examen_neurologique'],['ORL','examen_orl'],['Ophtalmologique','examen_ophtalmologique'],['Dermatologique','examen_dermatologique'],['Ostéo-articulaire','examen_osteoarticulaire'],['Urologique','examen_urologique']]
                .concat(patient?.sexe === 'F' ? [['Gynécologique','examen_gynecologique']] : [])
                .map(([label, field]) => (
                <Field key={field} label={label}>{txt(field, 2, `Examen ${label.toLowerCase()}...`)}</Field>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Module Diabète */}
        {hasDiabetes && (
          <AccordionItem value="diabete" className="bg-orange-50 border-2 border-orange-300 rounded-xl mb-3 overflow-hidden px-0">
            <AccordionTrigger className="px-5 py-4 font-heading font-semibold text-orange-800 hover:no-underline hover:bg-orange-100">
              Module Diabète
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Type de diabète">
                  <select value={form.diabete_type || ''} onChange={set('diabete_type')}
                    className="px-3 py-2 border border-orange-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-400/20">
                    <option value="">Sélectionner...</option>
                    {['Type 1','Type 2','Gestationnel','LADA','Autre'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Glycémie à jeun (mg/dL)">{inp('diabete_glycemie_a_jeun','number')}</Field>
                <Field label="Glycémie post-prandiale (mg/dL)">{inp('diabete_glycemie_post_prandiale','number')}</Field>
                <Field label="HbA1c (%)">
                  <div className="relative">
                    {inp('diabete_hba1c','number')}
                    {form.diabete_hba1c && <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold ${parseFloat(form.diabete_hba1c) > 6.5 ? 'text-red-500' : 'text-emerald-500'}`}>{parseFloat(form.diabete_hba1c) > 6.5 ? 'Élevé' : 'OK'}</span>}
                  </div>
                </Field>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4">
                <Field label="Traitement en cours">{txt('diabete_traitement', 2, 'Médicaments, insuline, régime...')}</Field>
                <Field label="Complications">{txt('diabete_complications', 2, 'Neuropathie, rétinopathie, néphropathie...')}</Field>
                <Field label="Objectifs thérapeutiques">{txt('diabete_objectifs', 2)}</Field>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Module Nutrition */}
        {hasNutrition && (
          <AccordionItem value="nutrition" className="bg-emerald-50 border-2 border-emerald-300 rounded-xl mb-3 overflow-hidden px-0">
            <AccordionTrigger className="px-5 py-4 font-heading font-semibold text-emerald-800 hover:no-underline hover:bg-emerald-100">
              Module Nutrition & Diète
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Tour de taille (cm)">{inp('nutrition_tour_de_taille','number')}</Field>
                <Field label="Objectif poids (kg)">{inp('nutrition_objectif_poids','number')}</Field>
                <Field label="Type de régime">
                  <select value={form.nutrition_type_regime || ''} onChange={set('nutrition_type_regime')}
                    className="px-3 py-2 border border-emerald-300 rounded-md text-sm bg-white focus:outline-none">
                    <option value="">Sélectionner...</option>
                    {['Équilibré','Hypocalorique','Hypoglucidique','Hyperprotéiné','Méditerranéen','Diabétique','Hyposodé','Sans gluten','Végétarien'].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
                <Field label="Apport calorique actuel (kcal)">{inp('nutrition_apport_calorique','number')}</Field>
                <Field label="Objectif calorique (kcal)">{inp('nutrition_objectif_calorique','number')}</Field>
                <Field label="Activité physique">{inp('nutrition_activite_physique','text','Sédentaire / Légère / Intense')}</Field>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Module HTA */}
        {hasHTA && (
          <AccordionItem value="hta" className="bg-red-50 border-2 border-red-300 rounded-xl mb-3 overflow-hidden px-0">
            <AccordionTrigger className="px-5 py-4 font-heading font-semibold text-red-800 hover:no-underline hover:bg-red-100">
              Module HTA (Hypertension Artérielle)
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Stade HTA">
                  <select value={form.hta_stade || ''} onChange={set('hta_stade')}
                    className="px-3 py-2 border border-red-300 rounded-md text-sm bg-white focus:outline-none">
                    <option value="">Sélectionner...</option>
                    {['HTA Grade 1 (légère)','HTA Grade 2 (modérée)','HTA Grade 3 (sévère)','HTA systolique isolée','HTA résistante'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Risque CV">
                  <select value={form.hta_risque_cardiovasculaire || ''} onChange={set('hta_risque_cardiovasculaire')}
                    className="px-3 py-2 border border-red-300 rounded-md text-sm bg-white focus:outline-none">
                    <option value="">Sélectionner...</option>
                    {['Faible','Modéré','Élevé','Très élevé'].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
                <Field label="TA cible max (mmHg)">{inp('hta_tension_max_cible','number','130')}</Field>
                <Field label="TA cible min (mmHg)">{inp('hta_tension_min_cible','number','80')}</Field>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4">
                <Field label="Traitement antihypertenseur">{txt('hta_traitement', 2, 'IEC, ARA2, bêta-bloquants, diurétiques...')}</Field>
                <Field label="Complications">{txt('hta_complications', 2, 'HVG, rétinopathie, néphropathie, AVCI...')}</Field>
                <Field label="Objectifs">{txt('hta_objectifs', 2)}</Field>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Module Néphrologie / Insuffisance rénale */}
        {hasRenal && (
          <AccordionItem value="renal" className="bg-cyan-50 border-2 border-cyan-300 rounded-xl mb-3 overflow-hidden px-0">
            <AccordionTrigger className="px-5 py-4 font-heading font-semibold text-cyan-800 hover:no-underline hover:bg-cyan-100">
              Module Néphrologie / Insuffisance rénale
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Créatininémie (mg/L)">{inp('renal_creatinine','number','12')}</Field>
                <Field label="DFG (mL/min/1.73m²)">
                  <div className="relative">
                    {inp('renal_dfg','number','90')}
                    {form.renal_dfg && <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold ${parseFloat(form.renal_dfg) < 60 ? 'text-red-500' : 'text-emerald-500'}`}>{parseFloat(form.renal_dfg) < 60 ? 'IRC' : 'OK'}</span>}
                  </div>
                </Field>
                <Field label="Stade IRC">
                  <select value={form.renal_stade || ''} onChange={set('renal_stade')}
                    className="px-3 py-2 border border-cyan-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400/20">
                    <option value="">Auto (selon DFG)...</option>
                    {['G1 (normal)','G2 (légère)','G3A (modérée)','G3B (modérée à sévère)','G4 (sévère)','G5 (terminale)'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Urée (g/L)">{inp('renal_uree','number','0.30')}</Field>
                <Field label="Kaliémie (mmol/L)">{inp('renal_kaliemie','number','4.2')}</Field>
                <Field label="Albuminurie / RAC (mg/g)">{inp('renal_albuminurie','number','30')}</Field>
                <Field label="Protéinurie">{inp('renal_proteinurie','text','0.5 g/24h, +, ++')}</Field>
                <Field label="Diurèse">{inp('renal_diurese','text','Conservée / Oligurie')}</Field>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4">
                <Field label="Traitement néphroprotecteur">{txt('renal_traitement', 2, 'IEC/ARA2, restriction sodée, EPO, chélateurs du phosphore...')}</Field>
                <Field label="Complications">{txt('renal_complications', 2, 'Anémie, troubles phosphocalciques, acidose, hyperkaliémie...')}</Field>
                <Field label="Objectifs / Conduite">{txt('renal_objectifs', 2, 'Cible TA, orientation néphrologue, préparation dialyse...')}</Field>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Conclusion */}
        <AccordionItem value="conclusion" className="bg-white border border-stone-200 rounded-xl mb-3 overflow-hidden px-0">
          <AccordionTrigger className="px-5 py-4 font-heading font-semibold text-stone-800 hover:no-underline hover:bg-stone-50">
            Conclusion & Diagnostic
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-5 space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wide">Diagnostic</label>
                <VoiceDictation
                  context="diagnostic médical et diagnostic différentiel"
                  onTranscription={t => setForm(f => ({ ...f, diagnostic: (f.diagnostic ? f.diagnostic + ' ' : '') + t }))}
                />
              </div>
              {txt('diagnostic', 3, 'Diagnostic principal et différentiels...')}
            </div>
            <Field label="Conduite à tenir (CAT)">{txt('conduite_a_tenir', 3, 'Traitement, conseils, orientation...')}</Field>
            <Field label="Examens complémentaires demandés">{txt('examens_complementaires', 2, 'NFS, bilan lipidique, ECG, échographie...')}</Field>
            <Field label="Notes cliniques">{txt('notes_cliniques', 2)}</Field>
          </AccordionContent>
        </AccordionItem>

        {/* Ordonnance intégrée */}
        {showPrescSection && (
          <AccordionItem value="ordonnance" className="bg-blue-50 border-2 border-blue-300 rounded-xl mb-3 overflow-hidden px-0">
            <AccordionTrigger className="px-5 py-4 font-heading font-semibold text-blue-800 hover:no-underline hover:bg-blue-100">
              Ordonnance
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs text-stone-500">Médicaments prescrits lors de cette consultation</p>
                <button onClick={() => setPrescMeds(m => [...m, { ...EMPTY_MED }])} data-testid="add-medication-button"
                  className="flex items-center gap-1 px-3 py-1.5 border border-blue-300 text-blue-600 hover:bg-blue-100 rounded-md text-xs font-medium transition-colors">
                  <Plus size={13} /> Ajouter médicament
                </button>
              </div>
              <div className="space-y-3">
                {prescMeds.map((m, i) => (
                  <div key={i} className="bg-white border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <MedicationAutocomplete
                          className="col-span-2"
                          inputClassName="w-full px-3 py-1.5 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:border-blue-400"
                          placeholder="Nom du médicament *"
                          value={m.nom}
                          onChange={nom => setPrescMeds(pm => pm.map((p,idx) => idx === i ? {...p, nom} : p))}
                          onPick={med => setPrescMeds(pm => pm.map((p,idx) => idx === i
                            ? {...p, nom: med.nom, dosage: p.dosage || [med.dosage, med.unite_dosage].filter(Boolean).join(' ')}
                            : p))}
                        />
                        <input placeholder="Dosage (ex: 500mg)" value={m.dosage}
                          onChange={e => setPrescMeds(pm => pm.map((p,idx) => idx === i ? {...p, dosage: e.target.value} : p))}
                          className="px-3 py-1.5 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:border-blue-400" />
                        <input placeholder="Durée (ex: 7 jours)" value={m.duree}
                          onChange={e => setPrescMeds(pm => pm.map((p,idx) => idx === i ? {...p, duree: e.target.value} : p))}
                          className="px-3 py-1.5 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:border-blue-400" />
                        <input placeholder="Posologie (ex: 1cp 3x/j après repas)" value={m.posologie}
                          onChange={e => setPrescMeds(pm => pm.map((p,idx) => idx === i ? {...p, posologie: e.target.value} : p))}
                          className="px-3 py-1.5 border border-stone-300 rounded-md text-sm bg-white col-span-2 focus:outline-none focus:border-blue-400" />
                      </div>
                      {prescMeds.length > 1 && (
                        <button onClick={() => setPrescMeds(pm => pm.filter((_,idx) => idx !== i))} className="p-1 text-stone-400 hover:text-red-500 mt-1"><X size={15} /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="flex flex-col space-y-1.5">
                  <label className="text-xs font-medium text-stone-600">Instructions patient</label>
                  <textarea rows={2} value={prescInstr} onChange={e => setPrescInstr(e.target.value)}
                    placeholder="Conseils, régime, suivi..."
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white resize-none focus:outline-none focus:border-blue-400" />
                </div>
                <div className="flex flex-col space-y-1.5">
                  <label className="text-xs font-medium text-stone-600">Notes internes</label>
                  <textarea rows={2} value={prescNotes} onChange={e => setPrescNotes(e.target.value)}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white resize-none focus:outline-none focus:border-blue-400" />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

      </Accordion>
    </div>
  );
}
